import { useDelivery } from 'api/hooks/useDelivery';
import { DeliveryStatus, TrayLabelInfo } from 'api/models/Deliveries';
import { OrderStatus } from 'api/models/Orders';
import Button from './Button';
import usePrintLabel from 'api/hooks/usePrintLabel';
import { useLanguage } from 'context/AppContext';
import { Set } from 'api/models/Sets';
import { useCallback, useMemo, useState } from 'react';
import MultiSelectModal from './MultiSelectModal';
import { SelectFieldOption } from './DropDown/ValidatingSelect';
import { DecantedTraysDetails, TraySummary } from 'api/models/Trays';

type Props = {
  deliveryId?: string | number;
  orderStatus?: OrderStatus;
  sets?: Set[];
  disabled?: boolean;
  onClick?: VoidFunction;
  width?: string | number;
};

const SHOW_PRINT_BLANK_TRAY_LABEL_BUTTON = true;
const HIDE_PRINT_TRAY_LABEL_BUTTON_DELIVERY_STATUSES: DeliveryStatus[] = ['DELETED', 'CANCELED'];

const HIDE_PRINT_TRAY_LABEL_BUTTON_ORDER_STATUSES: OrderStatus[] = ['DELETED', 'CANCELED'];

const PrintTrayLabelButton = ({
  deliveryId = '',
  orderStatus,
  sets: _sets,
  disabled,
  onClick,
  width = '140px',
}: Props) => {
  const lang = useLanguage()[0].PrintTrayLabelButton;

  const [showSelectTraysModal, setShowSelectTraysModal] = useState(false);

  const { data: delivery } = useDelivery(deliveryId);

  const sets = useMemo(() => _sets || delivery?.sets || [], [_sets, delivery?.sets]);

  // tray labels are printable if the set was checked in
  const setIsWanted = useCallback(
    (set: Set) =>
      set.inspections?.CHECK_IN?.status == 'CONFIRMED' /*&& set.inspections?.CHECK_OUT?.status != 'CONFIRMED'*/,
    [],
  );

  const allWantedTrays = useMemo(
    () =>
      sets.reduce<TrayLabelInfo[]>((acc, set) => {
        if (!setIsWanted(set)) {
          return acc;
        }

        set.trays
          .filter(t => t.name)
          .forEach(tray => {
            const decantedTrays = tray.inspections[0]?.decantedTrays;
            const trayNumbers: (TraySummary | DecantedTraysDetails)[] = decantedTrays?.length ? decantedTrays : [tray];
            trayNumbers.forEach((tray_, idx) => {
              acc.push({
                id: [tray.trayInstanceId, tray_.trayReference || -(idx + 1)].join('_'),
                setName: set.name || set.displayCode,
                trayNumber: tray_.trayReference ?? 0,
                assetNumber: tray_.assetNumber || undefined,
              });
            });
          });
        return acc;
      }, []),
    [setIsWanted, sets],
  );

  const selectTrayOptions = useMemo<SelectFieldOption[]>(() => {
    const options = allWantedTrays.map(tray => {
      const setName = tray.setName.length > 24 ? `${tray.setName.substring(0, 23)}…` : tray.setName;
      return {
        id: tray.id as string,
        label: `${setName} - ${lang.tray} ${tray.trayNumber || '-'}`,
      };
    });

    if (!SHOW_PRINT_BLANK_TRAY_LABEL_BUTTON && !options.length) {
      options.push({ id: '', label: lang.universalTrayLabel });
    }

    return options;
  }, [allWantedTrays, lang.universalTrayLabel, lang.tray]);

  const { openPrintTrayLabelsInNewTab, loading: fetchPrintLabelLoading } = usePrintLabel();

  // RULE: If a delivery or surgery that is not cancelled or deleted has any sets that are checked in and not yet checked out then show the print tray label button
  const showPrintLabelButton = useMemo(() => {
    if (orderStatus && HIDE_PRINT_TRAY_LABEL_BUTTON_ORDER_STATUSES.includes(orderStatus)) {
      return false;
    }

    if (delivery?.status && HIDE_PRINT_TRAY_LABEL_BUTTON_DELIVERY_STATUSES.includes(delivery.status)) {
      return false;
    }

    return sets.some(setIsWanted);
  }, [sets, orderStatus, delivery?.status, setIsWanted]);

  const handleConfirm = useCallback(
    async (selectedItems: SelectFieldOption[] = []) => {
      const trayLabelInfos = selectedItems
        .filter(item => item.id)
        .map(item => allWantedTrays.find(tray => tray.id == item.id) as TrayLabelInfo);

      await openPrintTrayLabelsInNewTab({
        deliveryId: '' + deliveryId,
        data: {
          trayLabelInfos,
          patientInitials: delivery?.patientInitials || '',
        },
      });

      setShowSelectTraysModal(false);
    },
    [allWantedTrays, delivery?.patientInitials, deliveryId, openPrintTrayLabelsInNewTab],
  );

  return showPrintLabelButton ? (
    <>
      <Button
        label={lang.printTrayLabels}
        onClick={() => {
          onClick?.();
          setShowSelectTraysModal(true);
        }}
        colors="white on active"
        disabled={disabled || fetchPrintLabelLoading}
        loading={fetchPrintLabelLoading}
        sx={{ width }}
      />
      {showSelectTraysModal && (
        <MultiSelectModal
          open
          title={lang.printTrayLabels}
          heading={lang.selectTrayLabels}
          body={
            SHOW_PRINT_BLANK_TRAY_LABEL_BUTTON ? (
              <Button label={lang.universalTrayLabel} colors="primary on white" onClick={() => handleConfirm()} />
            ) : null
          }
          items={selectTrayOptions}
          idKey="id"
          labelKey="label"
          onClose={() => setShowSelectTraysModal(false)}
          onConfirm={handleConfirm}
          confirmLabel={lang.printButton}
          loading={fetchPrintLabelLoading}
          loadingText={lang.pleaseWait}
        />
      )}
    </>
  ) : null;
};

export default PrintTrayLabelButton;
